import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tree, message } from "antd";
import { UserOutlined, HomeOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { getAllClients, getClientInfo, getDomainsByManager, getExploitationsByManager, getRoles } from "../../Utils/client/client";
import MapIcon from "../../assets/icons/007-map.png";

const MapItems = ({ collapsed }) => {
  const navigate = useNavigate();
  const { userRole, userInfo } = useSelector((state) => state.user);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);

  //get role ids from the env file



  const userRoleName = userRole?.name;
  const userLink = () => {
    if (userRoleName === 'client') {
      return '/client';
    } else if (userRoleName === 'admin' || userRoleName === 'superadmin') {
      return '/admin';
    } else if (userRoleName === 'domain_manager') {
      return '/domain-manager';
    } else if (userRoleName === 'exploitation_manager') {
      return '/exploitation-manager';
    }
  }


  const fetchRoles = async () => {
    try {

      const res = await getRoles();
      return res.data;
    } catch (error) {
      console.error(error);
    }
  }


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const roles = await fetchRoles();

        let data;

        switch (userRoleName) {
          // Admin or Super Admin
          case 'admin':
          case 'superadmin':
            data = await getAllClients().then((res) => res.data);
            // filter to display only clients with a role client
            const client_role_id = roles.find(role => role.name === 'client').role_id;
            data = data.filter(client => client.user?.role_id === client_role_id);
            break;
          case 'client':
            data = await getClientInfo(userInfo?.user_id).then((res) => [res.data]);
            break;
          case 'domain_manager':
            data = await getDomainsByManager(userInfo?.user_id).then((res) => res.data);
            break;
          case 'exploitation_manager':
            data = await getExploitationsByManager(userInfo?.user_id).then((res) => res.data);
            break;
          default:
            throw new Error("Invalid user role");
        }

        const formattedData = formatDataByRole(data, userRoleName);
        setTreeData(formattedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to load map data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userRoleName, userInfo?.user_id]);



  const formatDataByRole = (data, name) => {
    if (!data) {
      return [];
    }
    switch (name) {
      case 'admin':
      case 'superadmin': // Admin or Super Admin
        return data?.map((client) => ({
          key: `client-${client.user.user_id}`,
          title: client.user.company_name,
          icon: <UserOutlined />,
          children: formatDomains(client.domains, client.exploitations),
        }));
      case 'client': // Client
        return formatDomains(data[0].domains, data[0].exploitations);
      case 'domain_manager': // Domain Manager
      case 'domain_manager': // Domain Manager
      return data.domains?.map((domain) => ({
        key: `domain-${domain.domain_id}`,
        title: domain.domain_name,
        icon: <HomeOutlined />,
        children: formatExploitations(data.exploitations.filter(exp => exp.domain_id === domain.domain_id)),
      }));
      case 'exploitation_manager': // Exploitation Manager
        return formatExploitations(data);
      default:
        return [];
    }
  };

  const formatDomains = (domains, exploitations) => {
    return domains.map((domain) => ({
      key: `domain-${domain.domain_id}`,
      title: domain.domain_name,
      icon: <HomeOutlined />,
      children: formatExploitations(exploitations.filter((exp) => exp.domain_id === domain.domain_id)),
    }));
  };

  const formatExploitations = (exploitations) => {
    return exploitations.map((exploitation) => ({
      key: `exploitation-${exploitation.exploitation_id}`,
      title: exploitation.exploitation_name,
      icon: <EnvironmentOutlined />,
      isLeaf: true,
    }));
  };

  const onSelect = (selectedKeys, info) => {
    if (selectedKeys.length > 0 && selectedKeys[0]) {
      const [type, id] = selectedKeys[0].split('-');
      const routePrefix = userLink();
      if (type === 'domain') {
        navigate(`${routePrefix}/map/domain/${id}`);
      } else if (type === 'exploitation') {
        navigate(`${routePrefix}/map/exploitation/${id}`);
      }
    } else {
      console.warn('No valid selection made');
    }
  };

  if (collapsed) {
    return null;
  }

  return (
    <div className="mt-4 bg-indigo-50 px-4">
      <Tree
        treeData={[
          {
            key: 'map',
            title: <span
              className="font-semibold px-2 py-1"
              onClick={() => navigate(`${userLink()}/map`)}
            >
              Map</span>,
            icon: <img src={MapIcon} alt="map" className="w-6 h-6" />,
            children: treeData,
          },
        ]}
        onSelect={onSelect}
        className="rounded-lg bg-indigo-50 shadow-sm"
        loading={loading}
        showIcon
        titleRender={(nodeData) => (
          <span className="text-gray-700 hover:text-blue-600 transition-colors duration-200">
            {nodeData.title}
          </span>
        )}
      />
    </div>
  );
};

export default MapItems;